import { ConversationSummary } from '../core/hub-types';

/** The filter chips over the conversation list: which kind of conversation to show. */
export type ChatListKind = 'all' | 'patients' | 'patientApp' | 'referrals' | 'billing' | 'professionals' | 'groups' | 'team' | 'support';

/**
 * Each kind: its chip, the conversations it holds, the tag on their rows and what the list says when it has none.
 * `team` is the practice's own people in direct and group messages; its groups are `groups`.
 */
export const LIST_KINDS: Record<ChatListKind, { label: string; types: readonly ConversationSummary['type'][]; tag: string; empty: string }> = {
  all: { label: 'All', types: [], tag: '', empty: 'No conversations yet.' },
  patients: { label: 'Patients', types: ['patient'], tag: 'Patient', empty: 'No patient chats yet.' },
  // the patient's own chat from the patient app: their words, not the practice's notes about them
  patientApp: { label: 'Patient app', types: ['patient_direct'], tag: 'Patient app', empty: 'No patient app chats yet.' },
  referrals: { label: 'Referrals', types: ['referral'], tag: 'Referral', empty: 'No referral chats yet.' },
  billing: { label: 'MedicAI Connect', types: ['billing'], tag: 'Connect', empty: 'No MedicAI Connect chats yet.' },
  professionals: { label: 'Medical professionals', types: ['network'], tag: 'Professional', empty: 'No medical professionals yet.' },
  groups: { label: 'Groups', types: ['channel'], tag: 'Group', empty: 'No groups yet.' },
  team: { label: 'Team', types: ['direct', 'group_dm'], tag: 'Team', empty: 'No messages yet.' },
  support: { label: 'Support', types: ['support'], tag: 'Support', empty: 'No support chats yet.' },
};

/** The chips after All, in order. Physicians see colleagues ("Medical professionals") first. */
export function chipOrder(professionalsFirst: boolean): Exclude<ChatListKind, 'all'>[] {
  const order: Exclude<ChatListKind, 'all'>[] = ['patients', 'patientApp', 'referrals', 'billing', 'professionals', 'groups', 'team', 'support'];
  return professionalsFirst ? ['professionals', ...order.filter((k) => k !== 'professionals')] : order;
}

export interface ChatListChip { kind: ChatListKind; label: string; count: number }

/** All, then each kind the practice has conversations of (Groups and Team always), with how many it holds. */
export function listChips(list: ConversationSummary[], professionalsFirst: boolean): ChatListChip[] {
  const chips: ChatListChip[] = [];
  for (const kind of chipOrder(professionalsFirst)) {
    const count = list.filter((c) => LIST_KINDS[kind].types.includes(c.type)).length;
    if (count || kind === 'groups' || kind === 'team') chips.push({ kind, label: LIST_KINDS[kind].label, count });
  }
  return [{ kind: 'all', label: LIST_KINDS.all.label, count: chips.reduce((n, c) => n + c.count, 0) }, ...chips];
}

const whenOf = (c: ConversationSummary) => Date.parse(c.lastMessageAt ?? c.createdAt ?? '') || 0;

/** Conversations a person may pin to the top of one practice's list (the hub refuses more: PIN_LIMIT). */
export const MAX_PINNED = 5;

const pinOf = (c: ConversationSummary) => (c.pinnedAt ? Date.parse(c.pinnedAt) || 1 : 0);

/**
 * The conversations a chip shows, in one list: the ones I pinned first (the latest pinned on top), then newest message
 * first whatever their kind, those with no message yet after them (newest first).
 */
export function listRows(list: ConversationSummary[], kind: ChatListKind): ConversationSummary[] {
  const rows = kind === 'all' ? list : list.filter((c) => LIST_KINDS[kind].types.includes(c.type));
  return [...rows].sort((a, b) =>
    pinOf(b) - pinOf(a) || Number(!!b.lastMessageAt) - Number(!!a.lastMessageAt) || whenOf(b) - whenOf(a));
}

/** The small label on a row saying what kind of conversation it is, as its chip names it. */
export function typeTag(c: ConversationSummary): string {
  return (Object.values(LIST_KINDS).find((k) => k.types.includes(c.type))?.tag) ?? '';
}

/** What the list says when nothing is shown. */
export function listEmptyText(kind: ChatListKind, searching: boolean): string {
  return searching ? 'No conversation matches.' : LIST_KINDS[kind].empty;
}
